import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { JustfixUser } from "state-machine";
import AuthClient, {
  SendLoginCodeOptions,
  VerifyMagicLinkResponse,
  VerifyStatusCode,
} from "./AuthClient";
import { authRequiredPaths } from "routes";
import { District } from "./APIDataTypes";
import { NETWORK_AUTH_ERROR, reportUnexpectedAuthError } from "./auth-errors";
import { NetworkError } from "error-reporting";

type UserOrError = {
  user?: JustfixUser;
  error?: string;
};

type StartLoginResult = {
  created?: boolean;
  error?: string;
};

export type UserContextProps = {
  user?: JustfixUser;
  startLogin: (email: string) => Promise<StartLoginResult | void>;
  sendLoginCode: (
    email: string,
    options?: SendLoginCodeOptions
  ) => Promise<{ error?: string } | void>;
  verifyOtp: (
    email: string,
    code: string,
    onSuccess?: (user: JustfixUser) => void | Promise<void>
  ) => Promise<UserOrError | void>;
  verifyMagicLink: (code: string, utmSource?: string) => Promise<VerifyMagicLinkResponse>;
  logout: (fromPath: string) => void;
  subscribeBuilding: (
    bbl: string,
    housenumber: string,
    streetname: string,
    zip: string,
    boro: string,
    _user?: JustfixUser,
    housenumber_display?: string,
    streetname_display?: string
  ) => Promise<void>;
  unsubscribeBuilding: (bbl: string) => void;
  subscribeDistrict: (district: District, _user?: JustfixUser) => void;
  unsubscribeDistrict: (subscription_id: string) => void;
  sendEmailChangeCode: (newEmail: string) => Promise<{ error?: string } | void>;
  verifyEmailChangeOtp: (newEmail: string, code: string) => Promise<UserOrError | void>;
  updateEmail: (newEmail: string) => void;
};

const initialState: UserContextProps = {
  startLogin: async (email: string) => {},
  sendLoginCode: async (email: string, options?: SendLoginCodeOptions) => {},
  verifyOtp: async (
    email: string,
    code: string,
    onSuccess?: (user: JustfixUser) => void | Promise<void>
  ) => {},
  verifyMagicLink: async (code: string, utmSource?: string) => ({
    statusCode: VerifyStatusCode.Unknown,
    statusText: "",
  }),
  logout: (fromPath: string) => {},
  subscribeBuilding: async (
    bbl: string,
    housenumber: string,
    streetname: string,
    zip: string,
    boro: string,
    _user?: JustfixUser,
    housenumber_display?: string,
    streetname_display?: string
  ) => {},
  unsubscribeBuilding: (bbl: string) => {},
  subscribeDistrict: (district: District, _user?: JustfixUser) => {},
  unsubscribeDistrict: (subscription_id: string) => {},
  sendEmailChangeCode: async (newEmail: string) => {},
  verifyEmailChangeOtp: async (newEmail: string, code: string) => {},
  updateEmail: (newEmail: string) => {},
};

export const UserContext = createContext<UserContextProps>(initialState);

const toJustfixUser = (raw: any): JustfixUser | undefined => {
  // `user === undefined` means fetch is still in flight (see LoginPage).
  // Logged-out auth_check returns a user-shaped object with no email — keep it.
  if (raw == null) return undefined;
  return {
    email: raw.email,
    verified: !!raw.verified,
    id: raw.id,
    type: raw.type,
    buildingSubscriptions: raw.buildingSubscriptions || raw.subscriptions || [],
    districtSubscriptions: raw.districtSubscriptions || raw.district_subscriptions || [],
    subscriptionLimit: raw.subscriptionLimit ?? raw.subscription_limit,
  };
};

const mapFetchedUser = (fetched: any): JustfixUser | undefined => {
  const normalized = toJustfixUser(fetched);
  if (!normalized) return;
  return {
    ...normalized,
    buildingSubscriptions: (normalized.buildingSubscriptions || []).map((s: any) => ({ ...s })),
    districtSubscriptions: (normalized.districtSubscriptions || []).map((s: any) => ({ ...s })),
  };
};

/**
 * Merge the mount-time auth_check into context without clobbering a session
 * established while that request was in flight (magic-link landing in a
 * fresh browser verifies in parallel with auth_check).
 */
export const applyAuthCheckUser = (
  current: JustfixUser | undefined,
  fetched: any
): JustfixUser | undefined => {
  if (current?.email) return current;
  return mapFetchedUser(fetched);
};

export const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<JustfixUser>();

  const updateUserSubscriptions = useCallback((_user: JustfixUser | undefined):
    | JustfixUser
    | undefined => {
    const updatedUser = mapFetchedUser(_user);
    if (!updatedUser) return;
    setUser(updatedUser);
    return updatedUser;
  }, []);

  useEffect(() => {
    const asyncFetchUser = async () => {
      const fetched = await AuthClient.fetchUser();
      setUser((current) => applyAuthCheckUser(current, fetched));
    };
    asyncFetchUser();
  }, []);

  const startLogin = useCallback(async (email: string) => {
    try {
      const response = await AuthClient.startLogin(email);
      if (response.error) {
        return { error: response.error };
      }
      return { created: response.created };
    } catch (e) {
      if (e instanceof NetworkError) {
        return { error: NETWORK_AUTH_ERROR };
      }
      reportUnexpectedAuthError(e);
      return { error: NETWORK_AUTH_ERROR };
    }
  }, []);

  const sendLoginCode = useCallback(async (email: string, options?: SendLoginCodeOptions) => {
    try {
      const response = await AuthClient.sendLoginCode(email, options);
      if (response.error) {
        return { error: response.error };
      }
    } catch (e) {
      if (e instanceof NetworkError) {
        return { error: NETWORK_AUTH_ERROR };
      }
      reportUnexpectedAuthError(e);
      return { error: NETWORK_AUTH_ERROR };
    }
  }, []);

  const verifyOtp = useCallback(
    async (
      email: string,
      code: string,
      onSuccess?: (user: JustfixUser) => void | Promise<void>
    ) => {
      try {
        const response = await AuthClient.verifyOtp(email, code);
        if (response.error || !response.user) {
          return { error: response.error || "Verification failed" };
        }
        const updatedUser = updateUserSubscriptions(response.user);
        if (onSuccess && updatedUser) await onSuccess(updatedUser);
        return { user: updatedUser };
      } catch (e) {
        if (e instanceof NetworkError) {
          return { error: NETWORK_AUTH_ERROR };
        }
        reportUnexpectedAuthError(e);
        return { error: NETWORK_AUTH_ERROR };
      }
    },
    [updateUserSubscriptions]
  );

  const verifyMagicLink = useCallback(
    async (code: string, utmSource?: string) => {
      const response = await AuthClient.verifyMagicLink(code, utmSource);
      if (response.statusCode === VerifyStatusCode.Success && response.user) {
        const updatedUser = updateUserSubscriptions(response.user);
        return { ...response, user: updatedUser };
      }
      return response;
    },
    [updateUserSubscriptions]
  );

  const logout = useCallback(async (fromPath: string) => {
    const asyncLogout = async () => {
      await AuthClient.logout();
      setUser(undefined);
      if (authRequiredPaths().includes(fromPath)) {
        document.location.href = `${window.location.origin}`;
      }
    };
    asyncLogout();
  }, []);

  const subscribeBuilding = useCallback(
    async (
      bbl: string,
      housenumber: string,
      streetname: string,
      zip: string,
      boro: string,
      _user?: JustfixUser,
      housenumber_display?: string,
      streetname_display?: string
    ) => {
      const currentUser = !!user?.email ? user : _user;
      if (currentUser) {
        const response = await AuthClient.subscribeBuilding(
          bbl,
          housenumber,
          streetname,
          zip,
          boro,
          housenumber_display,
          streetname_display
        );
        setUser({ ...currentUser, buildingSubscriptions: response["building_subscriptions"] });
      }
    },
    [user]
  );

  const unsubscribeBuilding = useCallback(
    (bbl: string) => {
      if (user) {
        const asyncUnsubscribe = async () => {
          const response = await AuthClient.unsubscribeBuilding(bbl);
          setUser({ ...user, buildingSubscriptions: response["building_subscriptions"] });
        };
        asyncUnsubscribe();
      }
    },
    [user]
  );

  const subscribeDistrict = useCallback(
    async (district: District, _user?: JustfixUser) => {
      const currentUser = !!user?.email ? user : _user;
      if (currentUser) {
        const asyncSubscribe = async () => {
          const response = await AuthClient.subscribeDistrict(district);
          setUser({ ...currentUser, districtSubscriptions: response["district_subscriptions"] });
        };
        await asyncSubscribe();
      }
    },
    [user]
  );

  const unsubscribeDistrict = useCallback(
    (subscription_id: string) => {
      if (user) {
        const asyncUnsubscribe = async () => {
          const response = await AuthClient.unsubscribeDistrict(subscription_id);
          setUser({ ...user, districtSubscriptions: response["district_subscriptions"] });
        };
        asyncUnsubscribe();
      }
    },
    [user]
  );

  const sendEmailChangeCode = useCallback(async (newEmail: string) => {
    try {
      const response = await AuthClient.sendEmailChangeCode(newEmail);
      if (response?.error) {
        return { error: response.error };
      }
    } catch (e) {
      if (e instanceof NetworkError) {
        return { error: NETWORK_AUTH_ERROR };
      }
      reportUnexpectedAuthError(e);
      return { error: NETWORK_AUTH_ERROR };
    }
  }, []);

  const verifyEmailChangeOtp = useCallback(
    async (newEmail: string, code: string) => {
      try {
        const response = await AuthClient.verifyEmailChangeOtp(newEmail, code);
        if (response?.error || !response?.user) {
          return { error: response?.error || "Verification failed" };
        }
        const updatedUser = updateUserSubscriptions(response.user);
        return { user: updatedUser };
      } catch (e) {
        if (e instanceof NetworkError) {
          return { error: NETWORK_AUTH_ERROR };
        }
        reportUnexpectedAuthError(e);
        return { error: NETWORK_AUTH_ERROR };
      }
    },
    [updateUserSubscriptions]
  );

  const updateEmail = useCallback(
    (email: string) => {
      if (user) {
        const asyncUpdateEmail = async () => {
          const response = await AuthClient.updateEmail(email);
          setUser({ ...user, email: response.email, verified: false });
        };
        asyncUpdateEmail();
      }
    },
    [user]
  );

  const providerValue = useMemo(
    () => ({
      user,
      startLogin,
      sendLoginCode,
      verifyOtp,
      verifyMagicLink,
      logout,
      subscribeBuilding,
      unsubscribeBuilding,
      subscribeDistrict,
      unsubscribeDistrict,
      sendEmailChangeCode,
      verifyEmailChangeOtp,
      updateEmail,
    }),
    [
      user,
      startLogin,
      sendLoginCode,
      verifyOtp,
      verifyMagicLink,
      logout,
      subscribeBuilding,
      unsubscribeBuilding,
      subscribeDistrict,
      unsubscribeDistrict,
      sendEmailChangeCode,
      verifyEmailChangeOtp,
      updateEmail,
    ]
  );

  return <UserContext.Provider value={providerValue}>{children}</UserContext.Provider>;
};
